import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { api, formatDate, STATUS_LABELS } from "@/lib/api";
import { Link } from "react-router-dom";
import { Inbox, Mail, Phone, Settings2, ArrowRight, AlertTriangle, CheckCircle2, Truck } from "lucide-react";

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) return;
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/quotes").then(({ data }) => setRecent(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-10">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-blue-900">/ Dashboard</div>
          <h1 className="font-serif-display text-4xl lg:text-5xl font-light tracking-tight mt-2 text-slate-900">
            Panoramica <span className="italic text-blue-900">richieste.</span>
          </h1>
        </div>

        {stats && !stats.email_configured && (
          <div data-testid="email-warning" className="border border-amber-300 bg-amber-50 p-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-amber-900">Email automatica non configurata</div>
              <div className="text-sm text-amber-800 mt-1">
                Aggiungi <code className="bg-amber-100 px-2 py-0.5">RESEND_API_KEY</code> in <code className="bg-amber-100 px-2 py-0.5">/app/backend/.env</code> e riavvia il backend per inviare automaticamente le email di conferma ai clienti. Tutte le richieste vengono comunque salvate qui.
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          <Stat label="Richieste totali" value={stats?.total ?? "—"} icon={Inbox} />
          <Stat label="Nuove da gestire" value={stats?.new ?? "—"} icon={AlertTriangle} accent="text-red-600" />
          <Stat label="In spedizione" value={stats?.in_spedizione ?? "—"} icon={Truck} accent="text-blue-900" />
          <Stat label="Consegnate" value={stats?.consegnata ?? "—"} icon={CheckCircle2} accent="text-emerald-600" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
          <Pill label="Per Motori" value={stats?.motore ?? 0} />
          <Pill label="Per Cambi" value={stats?.cambio ?? 0} />
          <Pill label="Contattate" value={stats?.contattato ?? 0} />
          <Pill label="Spedite" value={stats?.spedita ?? 0} accent="text-purple-700" />
          <Pill label="Oggi" value={stats?.today ?? 0} accent="text-red-600" />
        </div>

        <section>
          <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-6">
            <div className="text-xs tracking-[0.3em] uppercase text-blue-900">Ultime richieste</div>
            <Link to="/admin/richieste" className="text-xs tracking-[0.2em] uppercase text-red-600 hover:text-red-700 inline-flex items-center gap-1">
              Vedi tutte <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 py-20 text-center text-slate-500">
              Nessuna richiesta ancora. Quando un cliente compilerà il modulo, la vedrai qui.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 divide-y divide-slate-200">
              {recent.map((q) => (
                <Link
                  key={q.id}
                  to="/admin/richieste"
                  data-testid={`recent-quote-${q.id}`}
                  className="grid grid-cols-12 gap-4 px-5 py-4 hover:bg-slate-50 items-center"
                >
                  <div className="col-span-3 text-xs text-slate-500">{formatDate(q.created_at)}</div>
                  <div className="col-span-3 truncate">
                    <div className="font-medium text-slate-900">{q.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5"><Phone className="w-3 h-3" />{q.phone}</div>
                  </div>
                  <div className="col-span-2 text-sm text-slate-700">
                    {q.part_type === "motore" ? "Motore" : "Cambio"}
                  </div>
                  <div className="col-span-2 font-mono text-xs text-slate-700">{q.engine_code}</div>
                  <div className="col-span-2 text-right">
                    <span className="text-[10px] tracking-wider uppercase border px-2 py-1 bg-slate-50 border-slate-200 text-slate-700">
                      {STATUS_LABELS[q.status]}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white p-6">
    <div className="flex items-center justify-between mb-4">
      <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500">{label}</span>
      <Icon className={`w-4 h-4 ${accent || "text-blue-900"}`} />
    </div>
    <div className={`font-serif-display text-4xl ${accent || "text-slate-900"}`}>{value}</div>
  </div>
);

const Pill = ({ label, value, accent }) => (
  <div className="bg-white p-5">
    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">{label}</div>
    <div className={`font-serif-display text-3xl ${accent || "text-blue-900"}`}>{value}</div>
  </div>
);
